import { ActivitySpinnerSize, ActivitySpinnerWidget, ListItem } from '@tableau/widgets';
import { DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';

import { Messages } from '~src/codegen/Localize';
import { createRowStore, defaultRowStructure } from '~src/commonInfra/components/dataGrid/SortableDataGrid3';
import { ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TypeUnsafeTranslation } from '~src/commonInfra/services/IReactTranslateService';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { ISiteGridAdminProfileCellProps, SiteMembershipGridColumns } from '~src/siteManagement/components/SiteMembershipGridColumns';
import { ISiteNameWithId } from '~src/siteManagement/serverApi/types/ISiteNameWithId';
import { SiteMembershipFilters } from '~src/userManagement/components/SiteMembershipFilters';
import { SiteMembershipDialogFilters } from '~src/userManagement/components/SiteRolesPickerContainer';
import { sortAdminProfilesByDisplayName } from '~src/userManagement/helpers/AdminProfileDropdownHelper';

import { IAdminProfile } from '../models/IAdminProfile';
import { AdminProfileSiteManagementDialogStyles } from '../styles/AdminProfileSiteManagementDialogStyles';

export const NONE_PROFILE_VALUE = '__none__';

export type AdminProfileUserSiteMembershipDialogProps = {
  readonly selectedUserName: string;
  readonly currentProfileId: string;
  readonly availableProfiles: IAdminProfile[];
};

export type AdminProfileUserSiteMembershipDialogState = {
  readonly sites: ISiteNameWithId[];
  readonly profileBySiteId: Record<string, string>;
  readonly loading: boolean;
  readonly searchQuery: string;
  readonly siteFilter: string;
};

export type AdminProfileUserSiteMembershipDialogBodyProps = ReactActionDialogBodyProps<
  AdminProfileUserSiteMembershipDialogProps,
  AdminProfileUserSiteMembershipDialogState
>;

const DataGridConfig: DataGrid.Config = {
  minimumColumnWidth: 38,
  maxInitialColumnWidth: -1,
};

const AdminProfileColumnHeader = (p: { col: { headerText: string } }) => (
  <span style={AdminProfileSiteManagementDialogStyles.AdminProfileColumnHeader}>{p.col.headerText}</span>
);

export function AdminProfileUserSiteMembershipDialogBody(
  props: AdminProfileUserSiteMembershipDialogBodyProps
): JSX.Element {
  const { sites, profileBySiteId, loading, searchQuery, siteFilter } = props.dialogState;
  const { availableProfiles, selectedUserName, actionBusy } = props;
  const { updateDialogState } = props;

  // Build the admin profile dropdown list items (memoized on availableProfiles).
  // `tooltip` becomes the option `title` in @tableau/widgets List (same pattern as getAdminProfileListItemsForSiteMembershipGrid).
  const dropdownListItems = React.useMemo((): ListItem[] => {
    const noneLabel: string = (Messages as any).adminProfileUserSiteMembershipNone?.() ?? 'None';
    return [{ value: NONE_PROFILE_VALUE, content: noneLabel, tooltip: noneLabel }].concat(
      sortAdminProfilesByDisplayName(availableProfiles).map(p => ({
        value: p.luid,
        content: p.name,
        tooltip: p.name,
      })),
    );
  }, [availableProfiles]);

  const handleProfileChange = React.useCallback(
    (siteId: string, value: string) => {
      updateDialogState({
        profileBySiteId: { ...profileBySiteId, [siteId]: value },
      });
    },
    [profileBySiteId, updateDialogState]
  );

  const handleFilterSelected = React.useCallback(
    (value: string) => updateDialogState({ siteFilter: value }),
    [updateDialogState]
  );

  const handleSearchChange = React.useCallback(
    (value: string) => updateDialogState({ searchQuery: value, siteFilter: SiteMembershipDialogFilters.AllSites }),
    [updateDialogState]
  );

  const handleCancelSearch = React.useCallback(
    () => updateDialogState({ searchQuery: '', siteFilter: SiteMembershipDialogFilters.AllSites }),
    [updateDialogState]
  );

  // Derive the filtered site list from dialog state.
  const filteredSites = React.useMemo((): ISiteNameWithId[] => {
    let result = sites;
    if (siteFilter === SiteMembershipDialogFilters.AssignedSites) {
      result = result.filter(s => profileBySiteId[s.id] && profileBySiteId[s.id] !== NONE_PROFILE_VALUE);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s => (s.name ?? s.urlName ?? '').toLowerCase().includes(q));
    }
    return result;
  }, [sites, profileBySiteId, siteFilter, searchQuery]);

  // Memoize the rowStore so DataGrid doesn't re-mount on every render.
  const rowStore = React.useMemo(
    () => createRowStore(() => SliceableHelper.fromArray(filteredSites), defaultRowStructure, []),
    [filteredSites]
  );

  // Build columns: Site Name + Admin Profile only.
  const columns = React.useMemo((): DataGrid.Column[] => {
    const adminProfileProps = (site: ISiteNameWithId): ISiteGridAdminProfileCellProps | null => {
      return {
        value: profileBySiteId[site.id] ?? NONE_PROFILE_VALUE,
        onChange: (value: string) => handleProfileChange(site.id, value),
        disabled: actionBusy,
        listItems: dropdownListItems.map(item => ({
          ...item,
          style: AdminProfileSiteManagementDialogStyles.AdminProfileDropdownListStyle,
        })),
      };
    };

    // Reuse the existing site-name and admin-profile cell renderers from SiteMembershipGridColumns.
    // We pass stub checkbox/dropdown props since those columns are omitted.
    const stubCheckboxProps = () => ({ checkboxState: 'checked' as any, clicked: () => {}, disabled: false });
    const stubDropdownProps = () => ({} as any);

    const allCols = SiteMembershipGridColumns.columns(stubDropdownProps, stubCheckboxProps, adminProfileProps);

    // Keep only the site-name column and the admin-profile column (drop select and site-role).
    const cols = allCols.filter(c => c.name === 'site-name-col' || c.name === 'admin-profile-col');

    return cols.map(c => {
      if (c.name === 'site-name-col') {
        return { ...c, growToFit: true, preferredWidth: undefined };
      }
      if (c.name === 'admin-profile-col') {
        return { ...c, growToFit: false, headerComponent: AdminProfileColumnHeader };
      }
      return c;
    });
  }, [profileBySiteId, dropdownListItems, actionBusy, handleProfileChange]);

  if (loading) {
    return (
      <div
        className={AdminProfileSiteManagementDialogStyles.DialogContentWrapper}
        data-tb-test-id="admin-profile-user-site-membership-dialog"
      >
        <div style={AdminProfileSiteManagementDialogStyles.LoadingSpinnerContainer}>
          <ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Large} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={AdminProfileSiteManagementDialogStyles.DialogContentWrapper}
      data-tb-test-id="admin-profile-user-site-membership-dialog"
    >
      <SiteMembershipFilters
        filters={[SiteMembershipDialogFilters.AllSites, SiteMembershipDialogFilters.AssignedSites]}
        onFilterSelected={handleFilterSelected}
        siteFilterSelection={siteFilter}
        onCancelSearch={handleCancelSearch}
        onSearchInputTextChange={handleSearchChange}
        searchInputText={searchQuery}
        translate={TypeUnsafeTranslation.translate}
      />
      <div className={AdminProfileSiteManagementDialogStyles.DataGridContainer}>
        <DataGrid
          cols={columns}
          config={DataGridConfig}
          showRowBanding={false}
          rowStore={rowStore}
          showHorizontalGridLines={false}
        />
      </div>
    </div>
  );
}
